import { cn } from "../ui/cn";
import { ActionRow } from "./ActionRow";
import { ReadTicks } from "./icons";
import type { GroupedMessage } from "./types";

const DEFAULT_TIME = "4:26 PM";

function tailRadius(isUser: boolean, isLastOfGroup: boolean) {
  if (!isLastOfGroup) return "rounded-[12px]";
  return isUser
    ? "rounded-[12px_12px_4px_12px]"
    : "rounded-[12px_12px_12px_4px]";
}

export function Bubble({ m }: { m: GroupedMessage }) {
  const isUser = m.from === "user";

  if (m.kind === "action") {
    return <ActionRow actions={m.actions} />;
  }

  const time = m.time ?? DEFAULT_TIME;
  const lines = m.text.split("\n");
  const hasImage = !!m.image;

  return (
    <div
      className={cn(
        "flex [animation:ss-bubble-in_380ms_cubic-bezier(0.16,1,0.3,1)]",
        isUser ? "justify-end" : "justify-start",
        m.isLastOfGroup ? "mb-2" : "mb-px",
      )}
    >
      <div
        className={cn(
          "text-[15.5px] leading-[1.4] text-white break-words relative overflow-hidden",
          // Image bubbles get a wider clamp so the photo reads as a real
          // attachment, not a thumbnail.
          hasImage ? "max-w-[88%] w-[280px]" : "max-w-[78%]",
          isUser ? "bg-tg-bubble-out" : "bg-tg-bubble-in",
          tailRadius(isUser, m.isLastOfGroup),
          hasImage ? "pt-1 pl-1 pr-1 pb-1.5" : "py-1.5 pl-3 pr-2.5",
        )}
      >
        {hasImage && (
          <img
            src={m.image}
            alt={m.imageAlt ?? ""}
            className="block w-full aspect-[4/3] object-cover rounded-[8px]"
            loading="lazy"
          />
        )}

        <div className={cn(hasImage && "px-2 pt-1.5")}>
          {lines.map((line, idx) => {
            const isLast = idx === lines.length - 1;
            if (!isLast) {
              return (
                <div key={idx} className="whitespace-pre-wrap">
                  {line}
                </div>
              );
            }
            return (
              <div
                key={idx}
                className="flex items-end flex-wrap gap-x-2 gap-y-0"
              >
                <span className="whitespace-pre-wrap">{line}</span>
                <span
                  className={cn(
                    "ml-auto pl-1.5 inline-flex items-center gap-[3px] text-[11px] leading-[1.6] whitespace-nowrap shrink-0",
                    isUser ? "text-tg-time-out" : "text-tg-time-in",
                  )}
                >
                  {time}
                  {isUser && <ReadTicks />}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
